import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  type AssetProjectCreateDto,
  type AssetProjectIdentifier,
  type AssetProjectUpdateDto
} from '../../api-schema-types/assets/AssetProjectCrudDtos'
import { type AssetProjectSummary } from '../../api-schema-types/assets/AssetProjectDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import { projectsApiMapper } from './projectApiMapper'

export interface projectSlugProps {
  projectUrlSlug: string | null
}

export interface ProjectsFilterProps {
  showInactiveProjects: boolean
}

export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/assets`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['SolarProjects', 'SolarProject'],

  endpoints: (builder) => ({
    getProjects: builder.query({
      query: ({ showInactiveProjects }: ProjectsFilterProps) => ({
        url: '/projects'
      }),
      providesTags: ['SolarProjects'],
      transformResponse: (response: AssetProjectSummary[], meta, arg: ProjectsFilterProps) => projectsApiMapper().projectSummaryListMapper(response, meta, arg)
    }),
    getProject: builder.query({
      query: ({ projectUrlSlug }: projectSlugProps) => ({
        url: `/projects/${projectUrlSlug}`
      }),
      providesTags: ['SolarProject'],
      transformResponse: (response: AssetProjectUpdateDto, meta, arg) => projectsApiMapper().projectDetailsMapper(response, meta, arg)
    }),
    update: builder.mutation<AssetProjectUpdateDto, Partial<AssetProjectUpdateDto>>({
      query: ({ ...patch }) => ({
        url: `/projects/${patch.urlSlug}`,
        method: 'put',
        body: patch
      }),
      invalidatesTags: ['SolarProjects', 'SolarProject']
    }),
    create: builder.mutation<AssetProjectIdentifier, AssetProjectCreateDto>({
      query: ({ ...post }) => ({
        url: '/projects/create',
        method: 'post',
        body: post
      }),
      invalidatesTags: ['SolarProjects']
    })
  })
})

export const useGetProjectsQuery = projectApi.endpoints.getProjects.useQuery
export const useGetProjectQuery = projectApi.endpoints.getProject.useQuery
export const useUpdateProjectMutation = projectApi.endpoints.update.useMutation
export const useCreateProjectMutation = projectApi.endpoints.create.useMutation
